import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { useAuth } from "@clerk/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const KEY = "middle_cookie_consent";
const VERSION = 1;
const OPEN_EVENT = "middle:cookie-settings";

export const openCookieSettings = () => window.dispatchEvent(new Event(OPEN_EVENT));

const stored = () => {
  try { const v = JSON.parse(localStorage.getItem(KEY) || "null"); return v?.version === VERSION ? v : null; } catch { return null; }
};

export const CookieBanner = () => {
  const { isSignedIn } = useAuth();
  const [open, setOpen] = useState(() => !stored());
  const gpc = typeof navigator !== "undefined" && navigator.globalPrivacyControl === true;
  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, show);
    return () => window.removeEventListener(OPEN_EVENT, show);
  }, []);
  const choose = (optional) => {
    const granted = optional && !gpc;
    localStorage.setItem(KEY, JSON.stringify({ version: VERSION, optional: granted, at: new Date().toISOString() }));
    if (isSignedIn) api.post("/privacy/consents", { purpose: "optional_cookies", granted, method: "cookie_banner" }).catch(() => {});
    setOpen(false);
  };
  if (!open) return null;
  const current = stored();
  return (
    <div role="dialog" aria-label="Cookie choices" className="fixed inset-x-3 bottom-20 z-40 mx-auto max-w-xl rounded-2xl border border-border bg-white p-4 shadow-xl fade-up sm:bottom-5" data-testid="cookie-banner">
      <div className="flex gap-3">
        <Cookie size={20} className="mt-0.5 shrink-0 text-amber-700" />
        <div className="space-y-2 text-sm text-slate-700">
          <p className="font-semibold text-slate-900">Cookies on Middle</p>
          <p>We use only strictly necessary cookies, to keep you signed in and secure. We don't use advertising or analytics cookies. If we ever add optional cookies, we'll only use them if you allow it. <Link to="/privacy-policy#cookies" className="underline" onClick={() => setOpen(false)} data-testid="cookie-banner-details">Cookie details</Link></p>
          {gpc && <p className="text-xs text-slate-500" data-testid="cookie-banner-gpc">Your browser's Global Privacy Control signal is on, so optional cookies stay off.</p>}
          {current && <p className="text-xs text-slate-500">Current choice: {current.optional ? "optional cookies allowed" : "necessary only"}.</p>}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <Button variant="outline" className="rounded-full" onClick={() => choose(false)} data-testid="cookie-necessary-btn">Necessary only</Button>
        <Button variant="outline" className="rounded-full" disabled={gpc} onClick={() => choose(true)} data-testid="cookie-accept-btn">Allow optional cookies</Button>
      </div>
    </div>
  );
};
